import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { ImagePlus, Loader2, Save, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const SettingsManager = () => {
  const [hero, setHero] = useState("");
  const [brand, setBrand] = useState("");
  const [phone, setPhone] = useState("");
  const [steps, setSteps] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setHero(r.data.hero_image_url || "");
      setBrand(r.data.brand_name || "");
      setPhone(r.data.phone || "");
      setSteps(r.data.how_steps || []);
    }).catch(() => {});
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHero(data.url);
      toast.success("Şəkil yükləndi — yadda saxlamağı unutmayın");
    } catch { toast.error("Şəkil yüklənmədi"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setStep = (i, key, val) => setSteps((s) => s.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const addStep = () => setSteps((s) => [...s, { title: "", text: "" }]);
  const removeStep = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { hero_image_url: hero, brand_name: brand, phone, how_steps: steps.filter((s) => s.title || s.text) });
      localStorage.setItem("hero", hero);
      localStorage.setItem("brand_name", brand);
      localStorage.setItem("phone", phone);
      toast.success("Ayarlar yadda saxlanıldı");
    } catch { toast.error("Xəta baş verdi"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-manager">
      <div className="glass rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-neutral-300 mb-1.5 block">Sayt adı</label>
          <Input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="settings-brand" className="bg-white/5 border-white/10 h-11" placeholder="iPhone Alış" />
        </div>
        <div>
          <label className="text-sm text-neutral-300 mb-1.5 block">Əlaqə nömrəsi</label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="settings-phone" className="bg-white/5 border-white/10 h-11" placeholder="077 600 97 97" />
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <h3 className="font-medium mb-1">Ana səhifə fon şəkli</h3>
        <p className="text-sm text-neutral-400 mb-4">Ana səhifədə göstərilən iPhone şəklini dəyişin.</p>
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4 flex items-center justify-center mb-4 min-h-[200px]">
          {hero ? <img src={fileUrl(hero)} alt="hero" className="max-h-56 object-contain" data-testid="settings-hero-preview" /> : <span className="text-neutral-600 text-sm">Şəkil yoxdur</span>}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
        <button onClick={() => fileRef.current?.click()} data-testid="settings-upload-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/15 text-sm text-neutral-300 hover:bg-white/5 transition-colors">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Şəkil yüklə
        </button>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium">"Necə işləyir?" addımları</h3>
          <button onClick={addStep} data-testid="settings-add-step" className="flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white transition-colors">
            <Plus className="w-4 h-4" /> Addım əlavə et
          </button>
        </div>
        <div className="space-y-3">
          {steps.map((s, i) => (
            <div key={i} className="rounded-xl border border-white/8 bg-white/[0.02] p-3 space-y-2" data-testid={`settings-step-${i}`}>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-xs shrink-0">{i + 1}</span>
                <Input value={s.title} onChange={(e) => setStep(i, "title", e.target.value)} placeholder="Başlıq" className="bg-white/5 border-white/10 h-9" />
                <button onClick={() => removeStep(i)} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
              <Textarea value={s.text} onChange={(e) => setStep(i, "text", e.target.value)} placeholder="Təsvir" className="bg-white/5 border-white/10 min-h-[60px]" />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save-btn"
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-neutral-100 text-black font-medium disabled:opacity-50 hover:bg-white transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Yadda saxla
      </button>
    </div>
  );
};
